import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon } from '../../components/Icon';
import { createAuthFor, syncAuthPassword } from '../../api';
import { generateTempPassword } from '../../core/helpers';


function CreateEmployeeModal({ data, onClose }) {
  const { employees, setEmps, setAllUsers, showToast, runSubmitting } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "employee", password: generateTempPassword(), permissions: {} });
  const colors = ["#0F766E", "#B45309", "#0369A1", "#7C3AED", "#DC2626", "#16A34A"];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.email) { showToast("Name and email required", "error"); return; }
    if (!form.password || form.password.length < 6) { showToast("Password must be at least 6 characters", "error"); return; }
    // Check for duplicate email
    const existing = await sb.select("users", `email=eq.${encodeURIComponent(form.email.toLowerCase())}`);
    if (existing.length > 0) { showToast("This email is already registered. Use a different email.", "error"); return; }
    if (form.phone) {
      const existingPhone = await sb.select("users", `phone=eq.${form.phone}`);
      if (existingPhone.length > 0) { showToast("This phone number is already registered.", "error"); return; }
    }

    await runSubmitting('Adding team member…', async () => {
    const newId = uuid(); // proper RFC4122 UUID
    const newEmp = {
      id: newId,
      name: form.name,
      email: form.email,
      phone: form.phone,
      role: form.role,
      password: form.password,
      avatar: initials(form.name),
      color: colors[employees.length % colors.length],
    };

    // Save to Supabase users table
    const result = await sb.createUser({
      id:       newId,
      email:    form.email.toLowerCase(),
      phone:    form.phone || null,
      name:     form.name,
      role:     form.role,
      avatar:   initials(form.name),
      color:    colors[employees.length % colors.length],
      must_change_password: true,
    });

    if (result.length > 0) {
      const authRes = await createAuthFor({ email: form.email, phone: form.phone, password: form.password, appUserId: newId, name: form.name, role: form.role });
      setEmps(es => [...es, newEmp]);
      setAllUsers(us => [...us, { ...newEmp, permissions: {} }]);
      if (!authRes.ok) showToast(`${form.name} saved, but login couldn't be created: ${authRes.error || "use Reset Password to set it"}.`, "warning");
      else showToast(`${form.name} added! Login: ${form.email} / ${form.password}`, "success");
      onClose();
    } else {
      showToast("Failed to save employee. Email may already exist.", "error");
    }
    }, 'employee');
  };

  return (
    <div className="modal-box" style={{ maxWidth: 480 }}>
      <div className="modal-head"><div className="modal-title">Add Team Member</div><button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button></div>
      <div className="modal-body">
        <div className="form-grid">
          <div className="f-group">
            <label className="f-label">Full Name <span className="f-req">*</span></label>
            <input className="f-input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Priya Sharma" />
          </div>
          <div className="f-group">
            <label className="f-label">Email <span className="f-req">*</span></label>
            <input className="f-input" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="emp@foundersbridge.in" />
          </div>
          <div className="f-group">
            <label className="f-label">Phone</label>
            <input className="f-input" type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="98xxxxxxxx" />
          </div>
          <div className="f-group">
            <label className="f-label">Login Password <span className="f-req">*</span></label>
            <input className="f-input" value={form.password} onChange={e => set("password", e.target.value)} placeholder="Min 6 characters" />
            <div className="f-hint">They will use this to login. They can change it later.</div>
          </div>
          <div className="f-group">
            <label className="f-label">Role</label>
            <select className="f-select" value={form.role} onChange={e => set("role", e.target.value)}>
              <option value="employee">Employee — sees only assigned clients</option>
              <option value="manager">Manager — sees all clients</option>
              <option value="admin">Admin — full access</option>
              <option value="sales">Sales — owns clients commercially</option>
            </select>
          </div>
          <div className="info-box" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
            <span><Icon name="info" size={16} /></span>
            <div style={{ fontSize: 12, color: "var(--blue)" }}>
              Share these login details:<br/>
              <strong>URL:</strong> {window.location.hostname}<br/>
              <strong>Email:</strong> {form.email || "their email"}<br/>
              <strong>Password:</strong> {form.password}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Add Team Member</button>
      </div>
    </div>
  );
}
/* eslint-disable */
// ═══════════════════════════════════════════════════════════════════════
// FOUNDERS BRIDGE CRM — SESSION 2 ADDITIONS
// Paste this BELOW the last line of App_Session1.jsx
// DO NOT paste above the existing code — append only
// ═══════════════════════════════════════════════════════════════════════
//
// New features added in Session 2:
// 1. Client Task Submission Flow (fill form / upload docs → team review)
// 2. Record Payment modal
// 3. Notifications centre (team + client)
// 4. Client Self-Registration
// 5. Reports page (downloadable summary)
// 6. WhatsApp notification triggers (Kraya)
// 7. Improved client portal with full task details
// 8. Password change modal
// ═══════════════════════════════════════════════════════════════════════
//
// INSTRUCTIONS TO DEPLOY:
// 1. Open App_Session1.jsx in Notepad
// 2. Scroll to the very bottom
// 3. Paste everything below this comment block
// 4. Save the file
// 5. Upload to GitHub as App.jsx (replace existing)
// ═══════════════════════════════════════════════════════════════════════

// ─── WHATSAPP / KRAYA SERVICE ─────────────────────────────────────────



export { CreateEmployeeModal };
